<template>
  <v-app id="app" class="app" v-show="isReady" :style="{minHeight: `${viewPort.height}px`}">
    <Header @menu:close="menuOpen = false" @menu:toggle="onMenuToggle" :menuOpen="menuOpen" v-if="!loading" />

    <transition :name="$root.platform.prerenderer ? '' : 'fade'">
      <ProjectContents v-if="!loading && $root.projectContentsOpen" :open.sync="$root.projectContentsOpen" />
    </transition>

    <FullscreenMenu @menu:close="menuOpen = false" :open="menuOpen" v-if="!loading" />

    <transition :name="$root.platform.prerenderer ? '' : 'fade'" mode="out-in">
      <PreloaderIntro v-if="loading || !isReady" @loading:complete="onLoadingComplete" />
      <v-container class="page-container fill-height px-0 py-0 container--fluid" :class="{ 'page-container--menu': menuOpen }" v-else>
        <transition :name="$root.platform.prerenderer ? '' : 'fade'" mode="out-in" appear>
          <router-view :key="routeKey" />
        </transition>
      </v-container>
    </transition>

    <transition name="fade">
      <Footer :menuOpen="menuOpen" v-if="!loading && footerVisible" />
    </transition>

    <BannerCookie v-if="!loading" />

    <v-overlay :value="fullScreenElement !== null" :opacity="1" color="#0e0e0e" z-index="1">
      <transition mode="out-in" name="complete-fade" appear>
        <div
          :key="`${fullScreenElement.projectIndex}-${fullScreenElement.contentIndex}-${fullScreenElement.imageIndex}`"
          v-if="fullScreenElement && fullScreenElement.type === 'project-vertical-image'"
          >
          <ProjectImage
            :style="fullScreenImageStyle"
            class="ma-0"
            contain
            :aspect-ratio="getAspect(fullScreenElement.chapter.projects[fullScreenElement.projectIndex].content[fullScreenElement.contentIndex].gallery[fullScreenElement.imageIndex].image)"
            :max-height="`${Math.floor(viewPort.height* 0.8)}px`"
            :max-width="`${Math.floor(viewPort.width * 0.8)}px`"
            :image="fullScreenElement.chapter.projects[fullScreenElement.projectIndex].content[fullScreenElement.contentIndex].gallery[fullScreenElement.imageIndex]"
            :author="fullScreenElement.chapter.projects[fullScreenElement.projectIndex].author"
            :id="`${fullScreenElement.projectIndex}-${fullScreenElement.contentIndex}-${fullScreenElement.imageIndex}`"
            :openCaptionID.sync="openCaptionID"
            />
        </div>
        <ProjectHorizontalGallery
          :key="`${fullScreenElement.projectIndex}-${fullScreenElement.contentIndex}`"
          v-else-if="fullScreenElement && fullScreenElement.type === 'project-horizontal-gallery'"
          :gallery="fullScreenElement.chapter.projects[fullScreenElement.projectIndex].content[fullScreenElement.contentIndex].gallery"
          :author="fullScreenElement.chapter.projects[fullScreenElement.projectIndex].author"
          :startDirection="fullScreenElement.startDirection"
          :startScroll="fullScreenElement.startScroll"
          :chapterScroll="chapterScroll"
          :containerHeight="fullScreenElement.height"
          />
      </transition>
    </v-overlay>

    <VersionInfo
      v-if="$root.process.env.SERVICE_STAGE !== 'production' && isNotLocal"
      :version="$root.process.env.GIT_VERSION"
      :commit-hash="$root.process.env.GIT_COMMIT_HASH"
      />
  </v-app>
</template>

<script>
import { VersionInfo, Viewport } from '@monogrid/vue-lib'
import { Platform } from '@monogrid/js-utils'
import shareImage from '@/assets/img/share.jpg'
import Header from '@/organisms/Header'
import Footer from '@/organisms/Footer'
import PreloaderIntro from '@/organisms/PreloaderIntro'
import FullscreenMenu from '@/molecules/FullscreenMenu'
import ProjectContents from '@/organisms/ProjectContents'
import BannerCookie from '@/atoms/BannerCookie'
import ProjectImage from '@/molecules/ProjectImage'
import ProjectHorizontalGallery from '@/molecules/ProjectHorizontalGallery'
import getAspect from '@/mixins/getAspect'

import Vue from 'vue'
import { createSimpleTransition } from 'vuetify/lib/components/transitions/createTransition'
const projectPhotoTransition = createSimpleTransition('project-photo-transition')
Vue.component('project-photo-transition', projectPhotoTransition)

export default {
  name: 'App',

  components: {
    BannerCookie,
    VersionInfo,
    Header,
    Footer,
    PreloaderIntro,
    FullscreenMenu,
    ProjectContents,
    ProjectImage,
    ProjectHorizontalGallery
  },

  mixins: [Viewport, getAspect],

  data: function () {
    return {
      isReady: false,
      menuOpen: false,
      footerVisible: true,
      loading: true, // window.location.href.indexOf('localhost') === -1,
      platform: Platform,
      fullScreenElement: null,
      chapterScroll: 0,
      openCaptionID: ''
    }
  },

  mounted: function () {
    this.isReady = true

    this.$router.beforeEach((to, from, next) => {
      this.$root.projectContentsOpen = false
      next()
      setTimeout(() => {
        this.menuOpen = false
      }, 100)
    })

    this.$events.on('chapter-scroll', (value) => {
      this.chapterScroll = value
    })

    this.$events.on('full-screen-element', (value) => {
      this.fullScreenElement = value
    })

    this.$events.on('set-project-contents-open', (value) => {
      this.$root.projectContentsOpen = value
    })
  },

  methods: {
    onLoadingComplete () {
      this.loading = false
    },

    onMenuToggle () {
      this.menuOpen = !this.menuOpen
    }
  },

  computed: {
    routeKey () {
      return `${this.$route.params.language}-${this.$route.params.scenarioSlug}-${this.$route.params.chapterSlug}`
    },
    isNotLocal () {
      return window.location.href.indexOf('localhost') === -1
    },
    fullScreenImageStyle () {
      const transY = 0
      return {
        width: `${this.viewPort.width}px`,
        position: 'relative',
        transform: `translate3d(0, ${transY}px, 0)`,
        transition: 'transform 0.5s'
      }
    }
  },

  watch: {
    isReady: function (value) {
      this.$root.preloader.visible = !value
    },

    fullScreenElement (value) {
      this.openCaptionID = ''
    },

    menuOpen () {
      document.querySelector('html').classList.toggle('application--dialog-opened', this.menuOpen)
    }
  },

  metaInfo: function () {
    return {
      // if no subcomponents specify a metaInfo.title, this title will be used
      title: ' ',
      // all titles will be injected into this template
      titleTemplate: 'The COVID-19 Visual Project',
      base: { href: '/' },
      link: [
        { rel: 'canonical', href: 'https://' + this.$root.process.env.SERVICE_TARGET_DOMAIN + this.$route.fullPath }
      ],
      meta: [
        { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
        { name: 'apple-mobile-web-app-capable', content: 'yes' },
        { name: 'mobile-web-app-capable', content: 'yes' },
        // twitter
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:site', content: 'https://' + this.$root.process.env.SERVICE_TARGET_DOMAIN + this.$route.fullPath },
        { name: 'twitter:title', content: 'The COVID-19 Visual Project' },
        { name: 'twitter:description', content: this.$gettext('An ongoing permanent archive on the coronavirus pandemic: effects, struggles, and glimmers of hope.') },
        { name: 'twitter:creator', content: '@CortonaOTM' },
        // generic description
        { vmid: 'description', name: 'description', content: this.$gettext('An ongoing permanent archive on the coronavirus pandemic: effects, struggles, and glimmers of hope.') },
        // Google / Schema.org markup:
        { vmid: 'itempropname', itemprop: 'name', content: 'The COVID-19 Visual Project' },
        { vmid: 'itempropdescription', itemprop: 'description', content: this.$gettext('An ongoing permanent archive on the coronavirus pandemic: effects, struggles, and glimmers of hope.') },
        { vmid: 'itempropimage', itemprop: 'image', content: 'https://' + this.$root.process.env.SERVICE_TARGET_DOMAIN + shareImage },
        // og tags
        { vmid: 'ogtype', property: 'og:type', content: 'website' },
        { vmid: 'ogurl', property: 'og:url', content: 'https://' + this.$root.process.env.SERVICE_TARGET_DOMAIN + this.$route.fullPath },
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: 'The COVID-19 Visual Project' },
        { vmid: 'ogdescription', property: 'og:description', content: this.$gettext('An ongoing permanent archive on the coronavirus pandemic: effects, struggles, and glimmers of hope.') },
        { vmid: 'ogimage', property: 'og:image', content: 'https://' + this.$root.process.env.SERVICE_TARGET_DOMAIN + shareImage },
        { vmid: 'ogimagetype', property: 'og:image:type', content: 'image/jpeg' },
        { vmid: 'ogimageheight', property: 'og:image:width', content: '1200' },
        { vmid: 'ogimagewidth', property: 'og:image:height', content: '630' }
      ],
      htmlAttrs: {
        lang: this.$language.current
      }
    }
  }
}
</script>

<style lang="scss">

html {
  overflow-y: auto !important;
}

html,
body,
.app {
  background-color: $c-gradient-bg;
  font-variant-ligatures: none;

  text-size-adjust: 100%;
  font-kerning: none;
  // min-height: -webkit-fill-available;

  -webkit-text-rendering: optimizeLegibility;
  -webkit-tap-highlight-color: transparent;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;

  // scroll-behavior: smooth;
  // overflow-x: hidden;
  // overflow-y: auto;

  &.application--dialog-opened {
    overflow: hidden;
  }
}

// .page-container {
//   height: 100%;
//   width: 100%;
//   left: 0;
//   top: 0;
//   will-change: transform;
//   backface-visibility: hidden;
//   z-index: 0;
//   overflow: hidden;

//   html.ie11 &,
//   html.mobile & {
//     overflow-y: auto;
//     overflow-x: hidden;
//     will-change: auto;
//   }
// }

// .page {
//   -webkit-overflow-scrolling: touch;
//   box-sizing: border-box;
//   position: absolute;
//   top: 0;
//   left: 0;

//   padding: 0;
//   margin: 0;
//   width: 100%;
//   overflow-x: hidden;

//   will-change: transform;
//   backface-visibility: hidden;

//   html.ie11 &,
//   html.mobile & {
//     position: relative;
//   }
// }

h1 {
  font-family: Barlow, sans-serif;
  font-size: 30px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.06;
  letter-spacing: normal;
  text-align: left;
  color: $c-light-grey;

  @include breakpoint('md-only') {
    font-size: 80px;
    letter-spacing: 0;
  }

  @include breakpoint('lg-and-up') {
    font-size: 100px;
    letter-spacing: 0;
  }
}

h2 {
  font-family: Barlow, sans-serif;
  font-size: 40px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.06;
  letter-spacing: normal;
  text-align: left;
  color: $c-light-grey;

  @include breakpoint('md-and-up') {
    text-align: left;
    font-family: 'Barlow', sans-serif;
    font-size: 60px;
    letter-spacing: 0;
    text-shadow: 0 3px 10px #00000029;
  }

  @include breakpoint('lg-and-up') {
    font-size: 80px;
  }
}

h3 {
  font-size: 24px;
  line-height: 1.29;
  font-family: Barlow, sans-serif;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: left;
  color: $c-light-grey;

  @include breakpoint('md-and-up') {
    line-height: 1.2;
    font-size: 42px;
  }
}

h4 {
  font-family: Barlow, sans-serif;
  font-size: 18px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.29;
  letter-spacing: normal;

  @include breakpoint('md-and-up') {
    font-size: 28px;
    letter-spacing: 0;
  }
}

h5 {
  font-family: Barlow, sans-serif;
  font-size: 16px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.2;
  letter-spacing: normal;
  color: $c-text-grey;

  @include breakpoint('md-and-up') {
    font-size: 30px;
  }
}

p {
  font-family: Lora;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  text-align: left;
  color: #908e8e;
  word-wrap: break-word;

  @include breakpoint('md-and-up') {
    font-size: 14px;
    letter-spacing: 0.17px;
    line-height: 1.5;
    margin-bottom: 30px !important;
  }

  @include breakpoint('lg-and-up') {
    font-size: 16px;
  }
}

p,
a,
span,
h1,
h2,
h3,
h4,
h5,
h6 {
  transition: color 0.25s;
}

strong {
  font-weight: 600 !important;
}

.big-text {
  font-size: 105px;
}

.color-gray {
  color: $c-text-grey;
}

.border-bottom-grey {
  border-bottom: 1px solid $c-grey;
}

.z-index-2 {
  z-index: 2;
}

.p-relative {
  position: relative;
}

.p-fixed {
  position: fixed;
}

.v-overlay.fullscreen .v-overlay__content {
  height: 100%;
  width: 100%;
}

.v-overlay.fullscreen-menu .v-overlay__content {
  height: 100%;
}

.v-application--wrap {
  min-height: inherit !important;
}

.v-image__image--preload {
  filter: blur(10px) !important;
}

.v-image.zoom-hover {
  @include breakpoint('md-and-up') {
    .v-image__image {
      transition: transform 1.2s $ease-out-cubic;
    }

    &:hover {
      .v-image__image {
        transform: scale(1.05);
      }
    }
  }
}

// .v-image {
//   .v-image__image {
//     transition: transform 0.5s $ease-out-cubic;
//   }
// }

.v-divider {
  border-color: $c-light-grey !important;
}

.overflow-hidden {
  overflow: hidden;
}

.full-height-vh {
  height: 100vh;
}

.fill-size {
  height: calc(100vh - 56px);
  width: 100vw;

  display: block;
}

a {
  color: $c-light-grey;
  text-decoration: none;
  font-size: 12px;

  @include breakpoint('md-and-up') {
    font-size: 14px;
  }

  @include breakpoint('lg-and-up') {
    font-size: 16px;
  }

  &.router-link-exact-active {
    color: $c-red;
  }
}

.v-btn.text-cta .v-btn__content {
  font-family: Barlow, sans-serif;
  font-size: 16px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.2;
  letter-spacing: normal;
  text-align: left;
  color: #d1000c;
  transition: color 0.2s;

  @include breakpoint('md-and-up') {
    font-size: 20px;
  }

  &:hover {
    color: $c-white;
  }
}

.tweened-progress-bar .v-progress-linear__determinate {
  transition: none !important;
}

.opencall-page .jury-name h4 {
  text-transform: uppercase;
}

.opencall-page .description p {
  @include breakpoint('sm-and-down') {
    font-size: 14px;
  }
}
/* transitions vue */

.fade-enter-active {
  transition: opacity 0.5s;
}

.fade-leave-active {
  transition: opacity 0s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.project-photo-transition-enter-active,
.project-photo-transition-leave-active {
  transition: opacity 0.5s;
}

.project-photo-transition-enter,
.project-photo-transition-leave-to {
  opacity: 0;
}

.complete-fade-enter-active,
.complete-fade-leave-active {
  transition: opacity 0.25s;
}

.complete-fade-enter,
.complete-fade-leave-to {
  opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active,
.slide-left-enter-active,
.slide-left-leave-active,
.slide-up-enter-active,
.slide-up-leave-active,
.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.15s, opacity 0.15s;
}

.slide-down-enter,
.slide-up-leave-to {
  transform: translate3d(0, -50%, 0);
  opacity: 0;
}

.slide-up-enter,
.slide-down-leave-to {
  transform: translate3d(0, 50%, 0);
  opacity: 0;
}

.slide-right-enter,
.slide-left-leave-to {
  transform: translate3d(-50%, 0, 0);
  opacity: 0;
}

.slide-left-enter,
.slide-right-leave-to {
  transform: translate3d(50%, 0, 0);
  opacity: 0;
}

/* keyframes */

@keyframes kenburns-top-left {
  0% {
    transform: scale(1) translate(0, 0);
    transform-origin: 16% 16%;
  }

  100% {
    transform: scale(1.05) translate(-15px, -15px);
    transform-origin: top left;
  }
}

</style>
